import React, { useMemo, useState } from 'react';

const MindMapMinimap = ({ nodes, edges, transform, canvasWidth, canvasHeight, onNavigate }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Base size and expanded size
  const baseWidth = 150;
  const baseHeight = 100;
  const minimapWidth = isHovered ? 450 : baseWidth;
  const minimapHeight = isHovered ? 300 : baseHeight;

  // Calculate bounds of all nodes
  const bounds = useMemo(() => {
    if (nodes.length === 0) {
      return { minX: 0, maxX: 800, minY: 0, maxY: 600 };
    }

    const nodeWidth = 180;
    const nodeHeight = 80;
    const padding = 100;

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    
    nodes.forEach(node => {
      minX = Math.min(minX, node.x);
      maxX = Math.max(maxX, node.x + nodeWidth);
      minY = Math.min(minY, node.y);
      maxY = Math.max(maxY, node.y + nodeHeight);
    });

    return {
      minX: minX - padding,
      maxX: maxX + padding,
      minY: minY - padding,
      maxY: maxY + padding,
    };
  }, [nodes]);

  const worldWidth = bounds.maxX - bounds.minX;
  const worldHeight = bounds.maxY - bounds.minY;
  const scaleX = minimapWidth / worldWidth;
  const scaleY = minimapHeight / worldHeight;
  const scale = Math.min(scaleX, scaleY);

  // Viewport rectangle
  const viewportX = (-transform.x / transform.scale - bounds.minX) * scale;
  const viewportY = (-transform.y / transform.scale - bounds.minY) * scale;
  const viewportWidth = (canvasWidth / transform.scale) * scale;
  const viewportHeight = (canvasHeight / transform.scale) * scale;

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    // Convert minimap click to world coordinates
    const worldX = clickX / scale + bounds.minX;
    const worldY = clickY / scale + bounds.minY;
    
    // Center the view on clicked point
    const newX = -(worldX * transform.scale - canvasWidth / 2);
    const newY = -(worldY * transform.scale - canvasHeight / 2);
    
    onNavigate({ x: newX, y: newY, scale: transform.scale });
  };

  return (
    <div 
      className="absolute right-4 bottom-4 bg-[#0f1216] border border-white/10 rounded-lg shadow-xl overflow-hidden cursor-pointer transition-all duration-200 ease-out origin-bottom-right"
      style={{ width: minimapWidth, height: minimapHeight }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <svg width={minimapWidth} height={minimapHeight}>
        {/* Background */}
        <rect width="100%" height="100%" fill="#0a0a0a" />
        
        {/* Connection Lines - Curved bezier paths matching main canvas */}
        {edges && edges.map(edge => {
          const sourceNode = nodes.find(n => n.id === edge.sourceId);
          const targetNode = nodes.find(n => n.id === edge.targetId);
          if (!sourceNode || !targetNode) return null;
          
          const nodeWidth = 200;
          const nodeHeight = 40;
          
          // Calculate center points (scaled to minimap)
          const x1 = (sourceNode.x - bounds.minX + nodeWidth / 2) * scale;
          const y1 = (sourceNode.y - bounds.minY + nodeHeight / 2) * scale;
          const x2 = (targetNode.x - bounds.minX + nodeWidth / 2) * scale;
          const y2 = (targetNode.y - bounds.minY + nodeHeight / 2) * scale;
          
          // Calculate bezier curve (same logic as MindMapEdge)
          const dx = x2 - x1;
          const dy = y2 - y1;
          
          let path;
          if (Math.abs(dx) > Math.abs(dy)) {
            // Horizontal bias - curve vertically
            const cp1x = x1 + dx * 0.25;
            const cp1y = y1;
            const cp2x = x2 - dx * 0.25;
            const cp2y = y2;
            path = `M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
          } else {
            // Vertical bias - curve horizontally
            const cp1x = x1;
            const cp1y = y1 + dy * 0.25;
            const cp2x = x2;
            const cp2y = y2 - dy * 0.25;
            path = `M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`;
          }
          
          const isCustom = edge.type === 'custom';
          
          return (
            <g key={edge.id}>
              {/* Glow effect */}
              <path
                d={path}
                fill="none"
                stroke={isCustom ? '#22c55e' : '#6b7280'}
                strokeWidth={isHovered ? 3 : 1}
                opacity={0.3}
                strokeLinecap="round"
              />
              {/* Main line */}
              <path
                d={path}
                fill="none"
                stroke={isCustom ? '#22c55e' : '#9ca3af'}
                strokeWidth={isHovered ? 1.5 : 0.5}
                strokeLinecap="round"
              />
            </g>
          );
        })}
        
        {/* Nodes */}
        {nodes.map(node => {
          const nodeColor = node.color || { bg: '#1e293b', border: '#334155' };
          const nodeWidth = 200;
          const nodeHeight = 40;
          const x = (node.x - bounds.minX) * scale;
          const y = (node.y - bounds.minY) * scale;
          const scaledWidth = nodeWidth * scale;
          const scaledHeight = nodeHeight * scale;
          
          return (
            <rect
              key={node.id}
              x={x}
              y={y}
              width={scaledWidth}
              height={scaledHeight}
              rx={4 * scale}
              fill={nodeColor.bg}
              stroke={nodeColor.border}
              strokeWidth={isHovered ? 1 : 0.5}
            />
          );
        })}

        {/* Viewport indicator */}
        <rect
          x={viewportX}
          y={viewportY}
          width={viewportWidth}
          height={viewportHeight}
          fill="rgba(139, 92, 246, 0.1)"
          stroke="rgba(139, 92, 246, 0.5)"
          strokeWidth={1}
          rx={2}
        />
      </svg>
    </div>
  );
};

export default MindMapMinimap;
